import { call, put, select } from 'redux-saga/effects';

import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import { fetchContentCardDefaultsInCurrentBoard } from './content-card-defaults';

export function* fetchContentTypeTemplates() {
  yield put(actions.fetchContentTypeTemplates());

  let items;
  let contentTypes = [];
  let pointSystems = [];

  try {
    const response = yield call(request, api.getContentTypeTemplates);
    items = response.items;
    contentTypes = (response.included && response.included.contentTypes) || [];
    pointSystems = (response.included && response.included.pointSystems) || [];
  } catch (error) {
    yield put(actions.fetchContentTypeTemplates.failure(error));
    return;
  }

  yield put(
    actions.fetchContentTypeTemplates.success(items || [], contentTypes, pointSystems),
  );

  if (contentTypes.length > 0) {
    // Группируем по pointSistemId для upsert через fetchContentTypes.success
    const byPs = contentTypes.reduce((acc, item) => {
      const key = String(item.pointSistemId);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    // eslint-disable-next-line no-restricted-syntax
    for (const [pointSystemId, list] of Object.entries(byPs)) {
      yield put(actions.fetchContentTypes.success(pointSystemId, list));
    }
  }
}

export function* createContentTypeTemplate(data) {
  yield put(actions.createContentTypeTemplate(data));

  let item;
  let contentTypes = [];

  try {
    const response = yield call(request, api.createContentTypeTemplate, data);
    item = response.item;
    contentTypes = (response.included && response.included.contentTypes) || [];
  } catch (error) {
    yield put(actions.createContentTypeTemplate.failure(error));
    return;
  }

  yield put(actions.createContentTypeTemplate.success(item, contentTypes));

  if (contentTypes.length > 0 && item) {
    yield put(actions.fetchContentTypes.success(item.id, contentTypes));
  }
}

export function* updateContentTypeTemplate(id, data) {
  yield put(actions.updateContentTypeTemplate(id, data));

  let item;
  let included = {};

  try {
    ({ item, included = {} } = yield call(request, api.updateContentTypeTemplate, id, data));
  } catch (error) {
    yield put(actions.updateContentTypeTemplate.failure(id, error));
    return;
  }

  yield put(
    actions.updateContentTypeTemplate.success(item, {
      pointSystems: included.pointSystems || [],
    }),
  );
}

export function* deleteContentTypeTemplate(id) {
  yield put(actions.deleteContentTypeTemplate(id));

  let item;

  try {
    ({ item } = yield call(request, api.deleteContentTypeTemplate, id));
  } catch (error) {
    yield put(actions.deleteContentTypeTemplate.failure(id, error));
    return;
  }

  yield put(actions.deleteContentTypeTemplate.success(item, {}));
}

/** @deprecated items = ContentType на шаблоне */
export function* createContentTypeTemplateItem(templateId, data) {
  yield put(actions.createContentType(templateId, data));

  let item;
  try {
    ({ item } = yield call(request, api.createContentType, templateId, data));
  } catch (error) {
    yield put(actions.createContentType.failure(templateId, error));
    return;
  }

  yield put(actions.createContentType.success(item, []));
}

export function* updateContentTypeTemplateItem(id, data) {
  yield call(updateContentType, id, data);
}

export function* deleteContentTypeTemplateItem(id) {
  yield call(deleteContentType, id);
}

export function* fetchContentTypesInCurrentBoard() {
  const pointSystem = yield select(selectors.selectPointSystemForSettingsModal);
  if (!pointSystem) {
    return;
  }

  yield put(actions.fetchContentTypes(pointSystem.id));

  let items;

  try {
    ({ items } = yield call(request, api.getContentTypes, pointSystem.id));
  } catch (error) {
    yield put(actions.fetchContentTypes.failure(pointSystem.id, error));
    return;
  }

  yield put(actions.fetchContentTypes.success(pointSystem.id, items));
}

export function* createContentTypeInCurrentBoard(data = {}) {
  const pointSystem = yield select(selectors.selectPointSystemForSettingsModal);
  if (!pointSystem) {
    return;
  }

  yield put(actions.createContentType(pointSystem.id, data));

  let item;

  try {
    ({ item } = yield call(request, api.createContentType, pointSystem.id, data));
  } catch (error) {
    yield put(actions.createContentType.failure(pointSystem.id, error));
    return;
  }

  yield put(actions.createContentType.success(item, []));
  yield call(fetchContentTypesInCurrentBoard);
}

export function* updateContentType(id, data) {
  yield put(actions.updateContentType(id, data));

  let item;

  try {
    ({ item } = yield call(request, api.updateContentType, id, data));
  } catch (error) {
    yield put(actions.updateContentType.failure(id, error));
    return;
  }

  yield put(actions.updateContentType.success(item, []));
}

export function* deleteContentType(id) {
  yield put(actions.deleteContentType(id));

  let item;

  try {
    ({ item } = yield call(request, api.deleteContentType, id));
  } catch (error) {
    yield put(actions.deleteContentType.failure(id, error));
    return;
  }

  yield put(actions.deleteContentType.success(item, []));
}

export function* applyContentTypeTemplateInCurrentBoard(templateId) {
  const { boardId } = yield select(selectors.selectPath);
  let pointSystem = yield select(selectors.selectBoardLevelPointSystemForCurrentBoard);

  if (!pointSystem && boardId) {
    yield put(actions.updateBoardPointSystem(boardId));
    try {
      ({ item: pointSystem } = yield call(request, api.updateBoardPointSystem, boardId, {}));
    } catch (error) {
      yield put(actions.updateBoardPointSystem.failure(boardId, error));
      return;
    }
    yield put(actions.updateBoardPointSystem.success(pointSystem));
  }

  if (!pointSystem) {
    return;
  }

  yield put(actions.applyContentTypeTemplate(pointSystem.id, templateId));

  let item;
  let contentTypes;
  let contentViewPlans;
  let contentCardDefaults;
  let employeePlans;
  let dampingCoefficients;
  let cardPointArchivedData;

  try {
    ({
      item,
      included: {
        contentTypes,
        contentViewPlans,
        contentCardDefaults,
        employeePlans,
        dampingCoefficients,
        cardPointArchivedData,
      } = {},
    } = yield call(request, api.applyContentTypeTemplate, pointSystem.id, templateId));
  } catch (error) {
    yield put(actions.applyContentTypeTemplate.failure(pointSystem.id, error));
    return;
  }

  yield put(
    actions.applyContentTypeTemplate.success(
      pointSystem.id,
      item,
      contentTypes || [],
      contentViewPlans || [],
      contentCardDefaults || [],
      cardPointArchivedData || [],
    ),
  );

  if (contentViewPlans) {
    yield put(actions.fetchContentViewPlans.success(pointSystem.id, contentViewPlans));
  }
  if (employeePlans) {
    yield put(actions.fetchEmployeePlans.success(pointSystem.id, employeePlans));
  }
  if (dampingCoefficients) {
    yield put(actions.fetchDampingCoefficients.success(pointSystem.id, dampingCoefficients));
  }
  if (contentCardDefaults) {
    yield put(actions.fetchContentCardDefaults.success(pointSystem.id, contentCardDefaults));
  }

  yield call(fetchContentTypesInCurrentBoard);
  yield call(fetchContentCardDefaultsInCurrentBoard);
}

export default {
  fetchContentTypeTemplates,
  createContentTypeTemplate,
  updateContentTypeTemplate,
  deleteContentTypeTemplate,
  createContentTypeTemplateItem,
  updateContentTypeTemplateItem,
  deleteContentTypeTemplateItem,
  fetchContentTypesInCurrentBoard,
  createContentTypeInCurrentBoard,
  updateContentType,
  deleteContentType,
  applyContentTypeTemplateInCurrentBoard,
};
